import { AgentJurisdiction } from 'src/app/models/agent-jurisdiction.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { TranslateService } from 'src/app/services/translate.service';

export function getVersionEffectivePeriodDisplay(
  row: AgentJurisdiction,
  dateFormatService: DateFormatService,
  translateService: TranslateService,
): string {
  const hasStart = row.effectiveStart != null && row.effectiveStart !== '';
  const hasEnd = row.effectiveEnd != null && row.effectiveEnd !== '';
  if (!hasStart && !hasEnd) {
    return translateService.getDynamicTranslation('Always in effect');
  }
  const startDisplay = hasStart
    ? dateFormatService.convertUTCDateStringToUserTimeZoneString(row.effectiveStart, dateFormatService.userDateFormat)
    : '';
  if (hasStart && !hasEnd) {
    return `${startDisplay} - ${translateService.getDynamicTranslation('Current')}`;
  }
  const endDisplay = dateFormatService.convertUTCDateStringToUserTimeZoneString(row.effectiveEnd, dateFormatService.userDateFormat);
  return `${startDisplay} - ${endDisplay}`;
}

export function isHistoricalVersion(row: AgentJurisdiction): boolean {
  return row.effectiveEnd != null && row.effectiveEnd !== '';
}

/**
 * DASH-2681: Current/Historical for a Version History flyout row (record-history snapshot) must be
 * driven by the backend `isCurrent` flag — true only for the single live/open version — NOT by the
 * row's snapshotted `effectiveEnd`. Every version's insert-snapshot is written while it is still open
 * (effectiveEnd = null), so deriving the label from the snapshot mislabels every insert row as
 * Current. Live AgentJurisdiction rows (the details popups) keep using isHistoricalVersion.
 */
export function isHistoricalHistoryRow(row: { isCurrent?: boolean }): boolean {
  return !row.isCurrent;
}

export function decorateAgentJurisdictionVersions(
  rows: AgentJurisdiction[],
  dateFormatService: DateFormatService,
  translateService: TranslateService,
): AgentJurisdiction[] {
  return [...rows]
    .map(row => {
      const historical = isHistoricalVersion(row);
      return {
        ...row,
        isHistorical: historical,
        statusLabel: historical ? 'Historical' : 'Current',
        statusClass: historical ? 'status-inactive' : 'status-active',
        effectivePeriodDisplay: getVersionEffectivePeriodDisplay(row, dateFormatService, translateService),
      };
    })
    .sort((a, b) => {
      const jurisdictionCompare = (a.jurisdictionId ?? '').localeCompare(b.jurisdictionId ?? '');
      if (jurisdictionCompare !== 0) {
        return jurisdictionCompare;
      }
      const oelTypeCompare = (a.oelTypeId ?? '').localeCompare(b.oelTypeId ?? '');
      if (oelTypeCompare !== 0) {
        return oelTypeCompare;
      }
      return (b.effectiveStart ?? '').localeCompare(a.effectiveStart ?? '');
    });
}
